import { useCallback, useEffect, useRef, useState } from 'react';

const COPIED_RESET_MS = 2000;

// Fallback for browsers (or insecure contexts) without the async clipboard API.
const copyWithTextArea = (text: string): boolean => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
};

export type CopyStatus = 'idle' | 'copied' | 'failed';

/** Copies text to the clipboard; `status` flips to `copied` (or `failed`) for a couple of seconds after each attempt. */
export const useCopyToClipboard = (): { copy: (text: string) => Promise<boolean>; status: CopyStatus } => {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = useCallback(async (text: string) => {
    let copied: boolean;

    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch {
      copied = copyWithTextArea(text);
    }

    setStatus(copied ? 'copied' : 'failed');
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setStatus('idle'), COPIED_RESET_MS);

    return copied;
  }, []);

  return { copy, status };
};
